import React, { createContext, useEffect, useMemo, useRef, useState } from 'react';
import type { InboundMessage, Locale, OutboundMessage, ThemeTokens } from './protocol';
import { BRIDGE_VERSION, sanitizeLocale } from './protocol';
import { postOutbound, subscribeInbound } from './transport';

type BridgeState = {
  initialized: boolean;
  token: string | null;
  tenant: string | null;
  locale: Locale;
  theme: ThemeTokens | null;
  flowId: string | null;
  entryStep: string | null;
  input: Record<string, unknown>;
  headers: Record<string, string>;
};

export type BridgeContextValue = BridgeState & {
  /** Send a message to the host. */
  emit: (msg: OutboundMessage) => void;
  /** Subscribe to host-initiated step jumps. Returns unsubscribe. */
  onGoto: (handler: (stepId: string) => void) => () => void;
  /** Subscribe to host back requests. Returns unsubscribe. */
  onBackRequest: (handler: () => void) => () => void;
};

const initialState: BridgeState = {
  initialized: false,
  token: null,
  tenant: null,
  locale: 'en',
  theme: null,
  flowId: null,
  entryStep: null,
  input: {},
  headers: {},
};

export const BridgeContext = createContext<BridgeContextValue | null>(null);

export const BridgeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<BridgeState>(initialState);

  // Stable Sets so subscribers don't trigger re-renders.
  const gotoListenersRef = useRef<Set<(stepId: string) => void>>(new Set());
  const backListenersRef = useRef<Set<() => void>>(new Set());

  useEffect(() => {
    const unsubscribe = subscribeInbound((msg: InboundMessage) => {
      switch (msg.type) {
        case 'init':
          setState({
            initialized: true,
            token: msg.token,
            tenant: msg.tenant,
            locale: sanitizeLocale(msg.locale),
            theme: msg.theme,
            flowId: msg.flowId,
            entryStep: msg.entryStep ?? null,
            input: msg.input ?? {},
            headers: msg.headers ?? {},
          });
          break;
        case 'token:update':
          setState((s) => ({ ...s, token: msg.token }));
          break;
        case 'theme:update':
          setState((s) => ({ ...s, theme: msg.theme }));
          break;
        case 'locale:update':
          setState((s) => ({ ...s, locale: sanitizeLocale(msg.locale) }));
          break;
        case 'goto':
          gotoListenersRef.current.forEach((fn) => fn(msg.stepId));
          break;
        case 'back:request':
          backListenersRef.current.forEach((fn) => fn());
          break;
      }
    });

    // Tell the host we are mounted. The host responds with `init`.
    // If the host has not attached its listener yet, that's fine: it should
    // send `init` on iframe.onload regardless of receiving `ready`.
    postOutbound({ type: 'ready', v: BRIDGE_VERSION });

    return unsubscribe;
  }, []);

  const value = useMemo<BridgeContextValue>(
    () => ({
      ...state,
      emit: postOutbound,
      onGoto: (handler) => {
        gotoListenersRef.current.add(handler);
        return () => {
          gotoListenersRef.current.delete(handler);
        };
      },
      onBackRequest: (handler) => {
        backListenersRef.current.add(handler);
        return () => {
          backListenersRef.current.delete(handler);
        };
      },
    }),
    [state],
  );

  return <BridgeContext.Provider value={value}>{children}</BridgeContext.Provider>;
};
